import {
    AccountAccount,
    AccountConsent,
    CirclePartner,
} from '@calm/evexia-web-sdk';
import { useAccessCode } from '@rhg/hooks/use-access-code';
import useAccount, { useLinkSamlAccounts } from '@rhg/hooks/use-account';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useChangeSponsorship } from '@rhg/hooks/use-change-sponsorship';
import { useGetCircle } from '@rhg/hooks/use-circle';
import useCircles from '@rhg/hooks/use-circles';
import { getConsentContext } from '@rhg/hooks/use-consent-context';
import { useCopyCircle } from '@rhg/hooks/use-copy-circle';
import { useDependentPartnerById } from '@rhg/hooks/use-dependent-partner';
import useHasAcceptedCohortTos from '@rhg/hooks/use-has-accepted-tos';
import { useInvitationByCode } from '@rhg/hooks/use-invitation';
import { useInviteCode } from '@rhg/hooks/use-invite-code';
import useJoinSponsorship from '@rhg/hooks/use-join-sponsorship';
import { useGetMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import { useCheckPartner } from '@rhg/hooks/use-partner';
import { useForceRefreshCircleRecommendations } from '@rhg/hooks/use-recommendations';
import {
    ConsentVersions,
    RegistrationFlow,
    SubmitFlowProps,
    SubmitFlowResult,
} from '@rhg/hooks/use-registration-context/types';
import { useGetUnsponsoredConfig } from '@rhg/hooks/use-unsponsored-config';
import { useUpdateAccountConsent } from '@rhg/hooks/use-update-consent';
import { objectEntries } from '@rhg/utils/arrays';
import { DEFAULT_COUNTRY_CODE } from '@rhg/utils/constants';
import {
    createInvitationError,
    getInvitationStatus,
    getInviteValidationFields,
    InvitationStatus,
} from '@rhg/utils/invitations';
import { getPartnerCircleSponsorshipUpsertRequest } from '@rhg/utils/partner';
import sleep from '@rhg/utils/sleep';
import {
    clearSAMLStorage,
    getPartnerPortalTokenData,
    getSSOSessionStorageData,
} from '@rhg/utils/sso';
import { useMutation } from '@tanstack/react-query';
import { useNeedsRegistrationConsent } from '../use-needs-registration-consent';

export const SSO_LINKING_ERROR = 'Missing SSO linking data';
export const MISSING_HIPAA_ERROR = 'Missing HIPAA signature';
export const JOIN_SPONSORSHIP_ERROR = 'Join Sponsorship Failed';
export const NO_CIRCLE_ERROR = 'No Circle to submit';

function getSSOValidationFields(samlToken: string, partner?: CirclePartner) {
    return getPartnerCircleSponsorshipUpsertRequest(
        partner,
        getPartnerPortalTokenData(samlToken || '')
    ).validationFields;
}

export function useSubmitFlow({
    account,
    accountConsents,
}: {
    account?: AccountAccount;
    accountConsents: AccountConsent[];
}) {
    const { refetch: refetchAccount } = useAccount();
    const { activeCircle, setActiveCircle } = useActiveCircle();
    const { joinSponsorshipAsync } = useJoinSponsorship();
    const { refetch: refetchCircles } = useCircles();
    const { getMostRecentSponsorshipAsync } = useGetMostRecentSponsorship();
    const { getUnsponsoredConfigAsync } = useGetUnsponsoredConfig();
    const { changeSponsorshipAsync } = useChangeSponsorship();
    const { forceRefreshRecommendationsAsync } =
        useForceRefreshCircleRecommendations();
    const { accessCode, setAccessCode } = useAccessCode();
    const { inviteCode } = useInviteCode();
    const { invitation } = useInvitationByCode(inviteCode);
    const { copyCircleAsync } = useCopyCircle();
    const { checkPartnerAsync } = useCheckPartner();
    const { dependentPartner: invitePartner, refetch: refetchInvitePartner } =
        useDependentPartnerById({
            dependentPartnerId: invitation?.attributes?.partnerId,
            countryCode: account?.countryCode ?? DEFAULT_COUNTRY_CODE,
        });
    const { updateConsentAsync } = useUpdateAccountConsent();
    const { linkSamlAccountsAsync: linkAccounts } = useLinkSamlAccounts();
    const { refetch: refetchConsent } = useHasAcceptedCohortTos();
    const { getCircleAsync: fetchCircle } = useGetCircle();
    const { ssoToken, isLinkedSsoUser } = getSSOSessionStorageData();
    const { setNeedsRegistrationConsent } = useNeedsRegistrationConsent();

    const { mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            eligibilityFormValues,
            registrationFlow,
            isSSO,
            willUseSSOTokenEligibility,
            isLinkingOnly,
            minConsentVersions,
            hipaaSignature,
            circle = activeCircle,
        }: SubmitFlowProps): Promise<SubmitFlowResult> => {
            async function runMutation() {
                let submissionCircleId: number | undefined =
                    circle?.recipient.id;
                const consents = objectEntries<ConsentVersions>(
                    minConsentVersions || ({} as ConsentVersions)
                ).map((args) => {
                    const [key, value] = args!;
                    return {
                        consentType: key,
                        consentVer: value,
                    };
                });

                if (isSSO && isLinkingOnly) {
                    try {
                        await linkAccounts();
                    } catch {
                        throw new Error(SSO_LINKING_ERROR);
                    }
                } else if (
                    registrationFlow === RegistrationFlow.LossOfSponsorship
                ) {
                    if (!hipaaSignature) {
                        throw new Error(MISSING_HIPAA_ERROR);
                    }
                    if (minConsentVersions?.chdpp) {
                        await updateConsentAsync({
                            id: accountConsents[0].consentId ?? 0,
                            chdppVersion: minConsentVersions?.chdpp ?? 0,
                        });
                    }
                    if (!circle) {
                        throw new Error(NO_CIRCLE_ERROR);
                    }
                    const response = await copyCircleAsync({
                        healthcarePortabilityVersion:
                            minConsentVersions?.healthcare_portability ?? 0,
                        consentSig: hipaaSignature,
                        circle,
                    });

                    submissionCircleId = response?.newRecipientId;
                    const newCircle = await fetchCircle(submissionCircleId!);
                    await forceRefreshRecommendationsAsync(newCircle);
                    await refetchCircles();
                    setActiveCircle(newCircle);
                } else if (
                    registrationFlow === RegistrationFlow.SkipSponsorship
                ) {
                    await updateConsentAsync({
                        id: accountConsents[0].consentId ?? 0,
                        chdppVersion: accountConsents[0]?.minCHDPPVersion ?? 0,
                    });
                } else if (
                    registrationFlow === RegistrationFlow.ChangeOfSponsorship
                ) {
                    if (isSSO && !isLinkedSsoUser) {
                        if (!ssoToken) {
                            throw new Error(SSO_LINKING_ERROR);
                        }

                        try {
                            await linkAccounts();
                        } catch {
                            throw new Error(SSO_LINKING_ERROR);
                        }
                    }
                    try {
                        if (!circle) {
                            throw new Error(NO_CIRCLE_ERROR);
                        }
                        const partner = await checkPartnerAsync({
                            circle,
                            code: accessCode,
                        });
                        const newCircle = await changeSponsorshipAsync({
                            partnerId: partner!.id,
                            circle,
                            willRefreshAllCircles: false,
                            request: {
                                parentPartnerId: partner?.parentPartner?.id,
                                validationFields:
                                    isSSO && willUseSSOTokenEligibility
                                        ? getSSOValidationFields(
                                              ssoToken ?? '',
                                              partner ?? undefined
                                          )
                                        : eligibilityFormValues,
                                consents,
                            },
                        });
                        submissionCircleId = newCircle.recipient.id;
                        await forceRefreshRecommendationsAsync(newCircle);
                        clearSAMLStorage();
                        await refetchCircles();
                        setActiveCircle(newCircle);
                    } catch (e) {
                        // Send users to error screen with support email on SSO sponsorship failure
                        if (isSSO && willUseSSOTokenEligibility) {
                            throw new Error(JOIN_SPONSORSHIP_ERROR);
                        } else {
                            throw e;
                        }
                    }
                } else if (
                    registrationFlow ===
                    RegistrationFlow.ChangeOfInviteeSponsorship
                ) {
                    if (
                        invitation &&
                        getInvitationStatus(invitation) ===
                            InvitationStatus.Pending &&
                        invitePartner
                    ) {
                        if (!circle) {
                            throw new Error(NO_CIRCLE_ERROR);
                        }
                        const newCircle = await changeSponsorshipAsync({
                            partnerId: invitePartner!.id,
                            circle,
                            willRefreshAllCircles: false,
                            request: {
                                parentPartnerId:
                                    invitePartner?.parentPartner?.id,
                                validationFields:
                                    getInviteValidationFields(inviteCode),
                                consents,
                            },
                        });
                        submissionCircleId = newCircle.recipient.id;
                        await forceRefreshRecommendationsAsync(newCircle);
                        clearSAMLStorage();
                        await refetchCircles();
                        setActiveCircle(newCircle);
                    } else {
                        throw createInvitationError(
                            invitation &&
                                getInvitationStatus(invitation) ===
                                    InvitationStatus.Claimed
                                ? 'claimed'
                                : 'expired'
                        );
                    }
                } else if (
                    registrationFlow ===
                        RegistrationFlow.NewInviteeRegistration ||
                    registrationFlow ===
                        RegistrationFlow.ExistingInviteeRegistration
                ) {
                    if (
                        invitation &&
                        getInvitationStatus(invitation) ===
                            InvitationStatus.Pending &&
                        invitePartner
                    ) {
                        if (!circle) {
                            throw new Error(NO_CIRCLE_ERROR);
                        }
                        // No SSO support needed
                        const newCircle = await joinSponsorshipAsync({
                            partnerId: invitePartner!.id,
                            circle,
                            request: {
                                parentPartnerId:
                                    invitePartner?.parentPartner?.id,
                                validationFields:
                                    getInviteValidationFields(inviteCode),
                                consents,
                            },
                            willRefreshAllCircles: false,
                        });
                        submissionCircleId = newCircle.recipient.id;
                        await forceRefreshRecommendationsAsync(newCircle);
                        refetchCircles();
                    } else {
                        throw createInvitationError(
                            invitation &&
                                getInvitationStatus(invitation) ===
                                    InvitationStatus.Claimed
                                ? 'claimed'
                                : 'expired'
                        );
                    }
                } else {
                    if (isSSO && !isLinkedSsoUser) {
                        if (!ssoToken) {
                            throw new Error(SSO_LINKING_ERROR);
                        }

                        try {
                            await linkAccounts();
                        } catch {
                            throw new Error(SSO_LINKING_ERROR);
                        }
                    }
                    try {
                        if (!circle) {
                            throw new Error(NO_CIRCLE_ERROR);
                        }
                        const partner = await checkPartnerAsync({
                            circle,
                            code: accessCode,
                        });
                        const newCircle = await joinSponsorshipAsync({
                            partnerId: partner!.id,
                            circle,
                            request: {
                                parentPartnerId: partner?.parentPartner?.id,
                                validationFields:
                                    isSSO && willUseSSOTokenEligibility
                                        ? getSSOValidationFields(
                                              ssoToken ?? '',
                                              partner ?? undefined
                                          )
                                        : eligibilityFormValues,
                                consents,
                            },
                            willRefreshAllCircles: false,
                        });
                        submissionCircleId = newCircle.recipient.id;
                        await forceRefreshRecommendationsAsync(newCircle);
                        refetchCircles();
                    } catch (e) {
                        // Send users to error screen with support email on SSO sponsorship failure
                        if (isSSO && willUseSSOTokenEligibility) {
                            throw new Error(JOIN_SPONSORSHIP_ERROR);
                        } else {
                            throw e;
                        }
                    }
                }

                // Need to flag that the user's consents should be shown in the registration flow, until registration is complete
                await setNeedsRegistrationConsent(true);

                const isLossOfSponsorship =
                    registrationFlow === RegistrationFlow.LossOfSponsorship;

                // No need to refresh if lost because we no longer have a sponsor or partner
                if (!isLossOfSponsorship) {
                    await Promise.all([
                        ...(accessCode && circle
                            ? [
                                  checkPartnerAsync({
                                      circle,
                                      code: accessCode,
                                  }),
                              ]
                            : []),
                        ...(inviteCode ? [refetchInvitePartner()] : []),
                    ]);
                }

                // Build post-submission consent requirements
                const refreshedSubmissionCircle = submissionCircleId
                    ? await fetchCircle(submissionCircleId)
                    : undefined;
                const accountConsentData = (await refetchConsent()).data;
                const newSponsorship =
                    !isLossOfSponsorship && refreshedSubmissionCircle
                        ? await getMostRecentSponsorshipAsync(
                              refreshedSubmissionCircle
                          )
                        : await getUnsponsoredConfigAsync();
                const consentRequirements = getConsentContext({
                    accountConsents: accountConsentData
                        ? [accountConsentData.consentData]
                        : [],
                    accountUserConsents: accountConsentData
                        ? [accountConsentData?.userResponse]
                        : [],
                    sponsorConsents: newSponsorship?.consents,
                });

                return {
                    consentRequirements,
                    circle: refreshedSubmissionCircle,
                    account: (await refetchAccount()).data,
                };
            }

            const shouldHaveMinLoading =
                registrationFlow === RegistrationFlow.LossOfSponsorship ||
                registrationFlow === RegistrationFlow.ChangeOfSponsorship ||
                registrationFlow ===
                    RegistrationFlow.ChangeOfInviteeSponsorship;
            const [submission] = await Promise.all([
                runMutation(),
                shouldHaveMinLoading && sleep(3000),
            ]);

            return submission;
        },
        onSuccess: () => {
            // Don't want to keep the SAML/SSO linking tokens around after success
            clearSAMLStorage();
            // Reset access code state
            setAccessCode('');
        },
        onError: (_, { isSSO, willUseSSOTokenEligibility }) => {
            // Don't want to keep SSO/SAML linking tokens around after non-manual failure so that the user isn't forced to link SSO when it will not work
            if (!isSSO || willUseSSOTokenEligibility) clearSAMLStorage();
        },
    });

    return {
        submitFlow: mutateAsync,
        ...otherProps,
    };
}
